import React, { useState, useEffect } from "react";
import AITextHelper from "../common/AITextHelper";
import AIBadge from "../common/AIBadge";

interface Lead {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  country?: string;
  language?: string;
  source?: string;
  status: string;
  importance: string;
  category: string;
  color_label: string;
  tags: string[];
  estimated_value: number;
  notes?: string;
  assigned_to?: string;
}

interface Props {
  lead: Lead | null;
  onClose: (refresh: boolean) => void;
}

const STATUSES   = ["new","contacted","qualified","proposal","won","lost"];
const IMPORTANCE = ["low","medium","high","critical"];
const CATEGORIES = ["retail","wholesale","distributor","b2b","hair_product","other"];
const COLORS     = ["blue","orange","red","green","purple","gray"];
const IMP_ICONS: Record<string,string> = { low:"⚫", medium:"🔵", high:"🟠", critical:"🔴" };

const CRMLeadForm: React.FC<Props> = ({ lead, onClose }) => {
  const [form, setForm] = useState<Lead>({
    name: "", email: "", phone: "", company: "", country: "", language: "",
    source: "", status: "new", importance: "medium", category: "other",
    color_label: "blue", tags: [], estimated_value: 0, notes: "", assigned_to: "",
  });
  const [tagInput, setTagInput]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => {
    if (lead) {
      setForm({ ...lead, tags: lead.tags || [] });
    }
  }, [lead]);

  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const removeTag = (t: string) => set("tags", form.tags.filter(x => x !== t));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Name is required"); return; }
    setSubmitting(true);
    setError(null);
    try {
      const url    = lead?.id ? `/api/crm/leads/${lead.id}` : "/api/crm/leads";
      const method = lead?.id ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Save failed");
      }
      onClose(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="crm-overlay" onClick={e => e.target === e.currentTarget && onClose(false)}>
      <div className="crm-modal">
        <div className="crm-modal-header">
          <h2>{lead?.id ? "Edit Lead" : "New Lead"}</h2>
          <button className="crm-btn-ghost" onClick={() => onClose(false)}>×</button>
        </div>

        {error && <div className="crm-form-error">{error}</div>}

        <form onSubmit={handleSubmit} className="crm-form">

          {/* Row 1 */}
          <div className="crm-form-row">
            <div className="crm-form-group crm-form-group-lg">
              <label>Name <span className="crm-required">*</span></label>
              <input autoFocus className="crm-input" value={form.name} onChange={e => set("name", e.target.value)} required />
            </div>
            <div className="crm-form-group">
              <label>Company</label>
              <input className="crm-input" value={form.company} onChange={e => set("company", e.target.value)} />
            </div>
          </div>

          {/* Row 2 */}
          <div className="crm-form-row">
            <div className="crm-form-group">
              <label>Phone</label>
              <input className="crm-input" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+971..." />
            </div>
            <div className="crm-form-group">
              <label>Email</label>
              <input type="email" className="crm-input" value={form.email} onChange={e => set("email", e.target.value)} />
            </div>
            <div className="crm-form-group">
              <label>Country</label>
              <input className="crm-input" value={form.country} onChange={e => set("country", e.target.value)} placeholder="UAE, Oman…" />
            </div>
          </div>

          {/* Row 3: Pipeline fields */}
          <div className="crm-form-row">
            <div className="crm-form-group">
              <label>Status</label>
              <select className="crm-select" value={form.status} onChange={e => set("status", e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
            <div className="crm-form-group">
              <label>Importance</label>
              <select className="crm-select" value={form.importance} onChange={e => set("importance", e.target.value)}>
                {IMPORTANCE.map(i => <option key={i} value={i}>{IMP_ICONS[i]} {i.charAt(0).toUpperCase()+i.slice(1)}</option>)}
              </select>
            </div>
            <div className="crm-form-group">
              <label>Category</label>
              <select className="crm-select" value={form.category} onChange={e => set("category", e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace("_"," ").replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
              </select>
            </div>
            <div className="crm-form-group">
              <label>Color Label</label>
              <select className="crm-select" value={form.color_label} onChange={e => set("color_label", e.target.value)}>
                {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Row 4 */}
          <div className="crm-form-row">
            <div className="crm-form-group">
              <label>Source</label>
              <input className="crm-input" value={form.source} onChange={e => set("source", e.target.value)} placeholder="whatsapp, website, referral…" />
            </div>
            <div className="crm-form-group">
              <label>Assigned To</label>
              <input className="crm-input" value={form.assigned_to} onChange={e => set("assigned_to", e.target.value)} placeholder="Sales rep" />
            </div>
            <div className="crm-form-group">
              <label>Est. Value (AED)</label>
              <input type="number" min="0" className="crm-input" value={form.estimated_value} onChange={e => set("estimated_value", parseFloat(e.target.value)||0)} />
            </div>
          </div>

          {/* Tags */}
          <div className="crm-form-group">
            <label>Tags</label>
            <div className="crm-tag-input-row">
              <input
                className="crm-input"
                placeholder="Type a tag and press Enter or Add"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              />
              <button type="button" className="crm-btn-ghost" onClick={addTag}>Add</button>
            </div>
            <div className="crm-tags" style={{ marginTop: 6 }}>
              {form.tags.map(t => (
                <span key={t} className="crm-tag crm-tag-removable">
                  {t}
                  <button type="button" onClick={() => removeTag(t)}>×</button>
                </span>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="crm-form-group">
            <label>Notes <AIBadge /></label>
            <AITextHelper
              className="crm-textarea"
              rows={3}
              value={form.notes || ""}
              onChange={e => set("notes", e.target.value)}
              placeholder="Any additional context…"
              context="CRM customer note"
              tone="professional"
            />
          </div>

          <div className="crm-form-actions">
            <button type="button" className="crm-btn-ghost" onClick={() => onClose(false)} disabled={submitting}>Cancel</button>
            <button type="submit" className="crm-btn-primary" disabled={submitting}>
              {submitting ? "Saving…" : lead?.id ? "Update Lead" : "Create Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CRMLeadForm;
